/**
 * HelpdeskPage.js — HR Help Desk
 *
 * Validations added:
 * 1. NewTicketModal: title required min5/max255, description required min10/max2000
 * 2. ArticleEditorModal: title required min5/max255, content required min50
 * 3. window.confirm in deleteArticle → ConfirmModal
 *
 * All other components (TicketDetail, StatsView, TicketsView filters,
 * send reply, close ticket rating) unchanged.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, Alert, Table, InputGroup,
} from 'react-bootstrap';
import {
    HelpCircle, Plus, Send, ChevronRight, Search,
    BookOpen, Clock, CheckCircle, AlertTriangle,
    BarChart2, MessageSquare, Star, RefreshCw,
    Tag, Eye, Edit2, Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];

const PRIORITY_CONFIG = {
    low:    { bg:'secondary', label:'Low',    sla:'120h' },
    medium: { bg:'info',      label:'Medium', sla:'48h'  },
    high:   { bg:'warning',   label:'High',   sla:'8h'   },
    urgent: { bg:'danger',    label:'Urgent', sla:'2h'   },
};
const STATUS_CONFIG = {
    open:             { bg:'primary',   label:'Open'           },
    in_progress:      { bg:'info',      label:'In Progress'    },
    pending_employee: { bg:'warning',   label:'Awaiting Reply' },
    resolved:         { bg:'success',   label:'Resolved'       },
    closed:           { bg:'secondary', label:'Closed'         },
};
const SLA_CONFIG = {
    ok:       { color:'#059669', label:''          },
    warning:  { color:'#d97706', label:'⚠ SLA Soon'},
    critical: { color:'#dc2626', label:'🚨 SLA!'   },
    breached: { color:'#991b1b', label:'💥 Breached'},
    resolved: { color:'#6b7280', label:''          },
    none:     { color:'#6b7280', label:''          },
};

const formatDate = (d) => d
    ? new Date(d).toLocaleString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})
    : '—';

// ── Shared Confirm Modal ──────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading,
    confirmLabel='Delete', confirmVariant='danger' }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant={confirmVariant} onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm"/> : confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);

const SlaBadge = ({ ticket }) => {
    const cfg = SLA_CONFIG[ticket.sla_status] || SLA_CONFIG.none;
    if (!cfg.label) return null;
    return <span style={{fontSize:10,color:cfg.color,fontWeight:600}}>{cfg.label}</span>;
};

const StarRating = ({ value, onChange, readOnly }) => (
    <div className="d-flex gap-1">
        {[1,2,3,4,5].map(n => (
            <Star key={n} size={20}
                style={{
                    cursor: readOnly ? 'default' : 'pointer',
                    color: n<=(value||0) ? '#f59e0b' : '#d1d5db',
                    fill:  n<=(value||0) ? '#f59e0b' : 'transparent',
                }}
                onClick={() => !readOnly && onChange && onChange(n)}/>
        ))}
    </div>
);

// ════════════════════════════════════════════════════════════════════════════
const HelpdeskPage = () => {
    const { user } = useAuth();
    const isHR     = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState('tickets');
    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <HelpCircle size={22} className="me-2 text-primary"/>HR Help Desk
                    </h4>
                    <p className="text-muted small mb-0">
                        {isHR ? 'Manage employee queries and knowledge base' : 'Raise and track your HR queries'}
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button size="sm" variant={tab==='tickets'?'primary':'outline-secondary'}
                        onClick={() => setTab('tickets')}>
                        <MessageSquare size={14} className="me-1"/>Tickets
                    </Button>
                    <Button size="sm" variant={tab==='kb'?'primary':'outline-secondary'}
                        onClick={() => setTab('kb')}>
                        <BookOpen size={14} className="me-1"/>Knowledge Base
                    </Button>
                    {isHR && (
                        <Button size="sm" variant={tab==='stats'?'primary':'outline-secondary'}
                            onClick={() => setTab('stats')}>
                            <BarChart2 size={14} className="me-1"/>Stats
                        </Button>
                    )}
                </div>
            </div>
            {tab==='tickets' && <TicketsView isHR={isHR}/>}
            {tab==='kb'      && <KnowledgeBase isHR={isHR}/>}
            {tab==='stats'   && isHR && <StatsView/>}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// TICKETS VIEW
// ════════════════════════════════════════════════════════════════════════════
const TicketsView = ({ isHR }) => {
    const [tickets,  setTickets]  = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [selected, setSelected] = useState(null);
    const [showNew,  setShowNew]  = useState(false);
    const [filters,  setFilters]  = useState({ status:'', priority:'', search:'' });

    const load = () => {
        setLoading(true);
        const endpoint = isHR ? '/helpdesk/tickets' : '/helpdesk/tickets/my';
        const params   = isHR ? filters : {};
        axiosInstance.get(endpoint, { params })
            .then(r => setTickets(r.data.data?.tickets || r.data.data || []))
            .catch(() => toast.error('Failed to load tickets'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [filters.status, filters.priority]);

    if (selected) return (
        <TicketDetail ticket={selected} isHR={isHR}
            onBack={() => setSelected(null)}
            onRefresh={() => { load(); setSelected(null); }}/>
    );

    return (
        <>
            <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                {isHR && (
                    <>
                        <InputGroup size="sm" style={{maxWidth:240}}>
                            <InputGroup.Text><Search size={14}/></InputGroup.Text>
                            <Form.Control placeholder="Search tickets…"
                                value={filters.search}
                                onChange={e => setFilters(p=>({...p,search:e.target.value}))}
                                onKeyDown={e => e.key==='Enter' && load()}/>
                        </InputGroup>
                        <Form.Select size="sm" style={{width:140}} value={filters.status}
                            onChange={e => setFilters(p=>({...p,status:e.target.value}))}>
                            <option value="">All status</option>
                            {Object.entries(STATUS_CONFIG).map(([k,v]) =>
                                <option key={k} value={k}>{v.label}</option>)}
                        </Form.Select>
                        <Form.Select size="sm" style={{width:130}} value={filters.priority}
                            onChange={e => setFilters(p=>({...p,priority:e.target.value}))}>
                            <option value="">All priority</option>
                            {Object.entries(PRIORITY_CONFIG).map(([k,v]) =>
                                <option key={k} value={k}>{v.label}</option>)}
                        </Form.Select>
                    </>
                )}
                <div className="ms-auto d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={load}><RefreshCw size={14}/></Button>
                    <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
                        <Plus size={14} className="me-1"/>New Ticket
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : tickets.length===0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <HelpCircle size={40} className="mx-auto mb-3 text-muted opacity-50"/>
                    <p className="text-muted">No tickets found.</p>
                    <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
                        <Plus size={14} className="me-1"/>Raise your first ticket
                    </Button>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:11}}>
                                <th className="ps-4">#</th>
                                {isHR && <th>Employee</th>}
                                <th>Subject</th><th>Category</th>
                                <th>Priority</th><th>Status</th>
                                <th>SLA</th><th>Replies</th>
                                <th className="pe-4 text-end">Opened</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tickets.map(t => (
                                <tr key={t.id} style={{cursor:'pointer'}} onClick={() => setSelected(t)}>
                                    <td className="ps-4">
                                        <span className="fw-mono text-muted small">{t.ticket_number}</span>
                                    </td>
                                    {isHR && (
                                        <td>
                                            <div className="fw-medium">{t.employee_name}</div>
                                            <div className="text-muted" style={{fontSize:10}}>{t.department}</div>
                                        </td>
                                    )}
                                    <td style={{maxWidth:220}}>
                                        <div className="fw-medium text-truncate">{t.title}</div>
                                        {t.sla_status==='breached' && <SlaBadge ticket={t}/>}
                                    </td>
                                    <td>
                                        {t.category_name
                                            ? <Badge bg="light" text="dark">{t.category_name}</Badge>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td>
                                        <Badge bg={PRIORITY_CONFIG[t.priority]?.bg||'secondary'}>
                                            {PRIORITY_CONFIG[t.priority]?.label||t.priority}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={STATUS_CONFIG[t.status]?.bg||'secondary'}>
                                            {STATUS_CONFIG[t.status]?.label||t.status}
                                        </Badge>
                                    </td>
                                    <td>
                                        {t.sla_due_at ? (
                                            <div style={{fontSize:11,color:SLA_CONFIG[t.sla_status]?.color}}>
                                                <SlaBadge ticket={t}/>
                                                {t.sla_hours_left!==null && !['resolved','closed'].includes(t.status) && (
                                                    <div className="text-muted" style={{fontSize:10}}>
                                                        {t.sla_hours_left>0
                                                            ? `${t.sla_hours_left}h left`
                                                            : `${Math.abs(t.sla_hours_left)}h overdue`}
                                                    </div>
                                                )}
                                            </div>
                                        ) : <span className="text-muted" style={{fontSize:11}}>—</span>}
                                    </td>
                                    <td><Badge bg="light" text="dark">{t.message_count||0}</Badge></td>
                                    <td className="pe-4 text-end text-muted" style={{fontSize:11}}>
                                        {formatDate(t.created_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {showNew && (
                <NewTicketModal
                    onClose={() => setShowNew(false)}
                    onDone={() => { setShowNew(false); load(); }}/>
            )}
        </>
    );
};

// ── New Ticket Modal — VALIDATIONS ────────────────────────────────────────
const validateTicket = (form) => {
    const e = {};
    const title = form.title?.trim();
    if (!title)              e.title = 'Subject is required';
    else if (title.length<5) e.title = 'Subject must be at least 5 characters';
    else if (title.length>255) e.title = 'Subject cannot exceed 255 characters';

    const desc = form.description?.trim();
    if (!desc)              e.description = 'Description is required';
    else if (desc.length<10) e.description = 'Please describe your issue in more detail (min 10 characters)';
    else if (desc.length>2000) e.description = 'Description cannot exceed 2000 characters';

    return e;
};

const NewTicketModal = ({ onClose, onDone }) => {
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({
        title:'', description:'', category_id:'', priority:'medium',
    });
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);

    useEffect(() => {
        axiosInstance.get('/helpdesk/categories')
            .then(r => setCategories(r.data.data||[]))
            .catch(()=>{});
    }, []);

    const setField = (k,v) => {
        setForm(p=>({...p,[k]:v}));
        if (attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; return n; });
    };

    const submit = async () => {
        setAttempted(true);
        const errs = validateTicket(form);
        if (Object.keys(errs).length>0) { setErrors(errs); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k,v]) => v && fd.append(k,v));
            const res = await axiosInstance.post('/helpdesk/tickets', fd);
            toast.success(`Ticket ${res.data.data?.ticket_number} created! HR will respond within ${res.data.data?.sla_hours}h`);
            onDone();
        } catch (err) { toast.error(err.response?.data?.message||'Failed to create ticket'); }
        finally { setSaving(false); }
    };

    const titleLen = form.title?.length||0;
    const descLen  = form.description?.length||0;

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <HelpCircle size={18} className="me-2 text-primary"/>Raise a Ticket
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="small py-2 mb-3">
                    Before raising a ticket, check our <strong>Knowledge Base</strong> — your question may already be answered!
                </Alert>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Subject * <span className="text-muted fw-normal">(min 5 chars)</span></span>
                            <span className={titleLen>230?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {titleLen}/255
                            </span>
                        </Form.Label>
                        <Form.Control
                            placeholder="e.g. Leave balance not updated"
                            value={form.title} maxLength={255}
                            isInvalid={attempted && !!errors.title}
                            onChange={e => setField('title', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Category</Form.Label>
                        <Form.Select value={form.category_id}
                            onChange={e => setField('category_id', e.target.value)}>
                            <option value="">— General —</option>
                            {categories.map(c =>
                                <option key={c.id} value={c.id}>{c.name} (SLA: {c.sla_hours}h)</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Priority</Form.Label>
                        <Form.Select value={form.priority}
                            onChange={e => setField('priority', e.target.value)}>
                            {Object.entries(PRIORITY_CONFIG).map(([k,v]) =>
                                <option key={k} value={k}>{v.label} (SLA: {v.sla})</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Description * <span className="text-muted fw-normal">(min 10 chars)</span></span>
                            <span className={descLen>1800?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {descLen}/2000
                            </span>
                        </Form.Label>
                        <Form.Control as="textarea" rows={4} maxLength={2000}
                            placeholder="Describe your issue in detail. Include dates, amounts, or error messages if relevant."
                            value={form.description}
                            isInvalid={attempted && !!errors.description}
                            onChange={e => setField('description', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Submit Ticket'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// TICKET DETAIL — unchanged
// ════════════════════════════════════════════════════════════════════════════
const TicketDetail = ({ ticket, isHR, onBack, onRefresh }) => {
    const { user }   = useAuth();
    const [data,     setData]    = useState(null);
    const [loading,  setLoading] = useState(true);
    const [reply,    setReply]   = useState('');
    const [internal, setInternal]= useState(false);
    const [sending,  setSending] = useState(false);
    const [rating,   setRating]  = useState(0);
    const [ratingComment, setRatingComment] = useState('');
    const [showClose, setShowClose] = useState(false);
    const messagesEndRef = useRef(null);

    const load = () => {
        setLoading(true);
        axiosInstance.get(`/helpdesk/tickets/${ticket.id}`)
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load ticket'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [ticket.id]);
    useEffect(() => { messagesEndRef.current?.scrollIntoView({behavior:'smooth'}); }, [data?.messages]);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            const fd = new FormData();
            fd.append('message', reply);
            fd.append('is_internal', String(internal));
            await axiosInstance.post(`/helpdesk/tickets/${ticket.id}/reply`, fd);
            toast.success(internal ? 'Internal note added' : 'Reply sent');
            setReply(''); load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed to send'); }
        finally { setSending(false); }
    };

    const changeStatus = async (status) => {
        try {
            await axiosInstance.patch(`/helpdesk/tickets/${ticket.id}/status`,{status});
            toast.success(`Status changed to ${status}`); load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };

    const closeTicket = async () => {
        try {
            await axiosInstance.post(`/helpdesk/tickets/${ticket.id}/close`,{
                satisfaction_rating:rating, satisfaction_comment:ratingComment,
            });
            toast.success('Ticket closed. Thank you!');
            setShowClose(false); onRefresh();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };

    const reopen = async () => {
        try {
            await axiosInstance.post(`/helpdesk/tickets/${ticket.id}/reopen`,{reason:'Issue not resolved'});
            toast.success('Ticket reopened'); load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (!data)   return null;

    const isMine   = data.employee_id === user?.id;
    const isActive = !['closed'].includes(data.status);

    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>← Back</Button>
            <Card className="border-0 shadow-sm mb-4 p-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                        <div className="text-muted small mb-1">
                            {data.ticket_number} · Opened {formatDate(data.created_at)}
                            {data.category_name && ` · ${data.category_name}`}
                        </div>
                        <h5 className="fw-bold mb-2">{data.title}</h5>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg={STATUS_CONFIG[data.status]?.bg||'secondary'}>
                                {STATUS_CONFIG[data.status]?.label}
                            </Badge>
                            <Badge bg={PRIORITY_CONFIG[data.priority]?.bg||'secondary'}>
                                {PRIORITY_CONFIG[data.priority]?.label}
                            </Badge>
                            {data.sla_status!=='none' && <SlaBadge ticket={data}/>}
                            {isHR && <span className="text-muted small">From: {data.employee_name} ({data.department})</span>}
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        {isHR && isActive && (
                            <Form.Select size="sm" style={{width:160}} value={data.status}
                                onChange={e => changeStatus(e.target.value)}>
                                {Object.entries(STATUS_CONFIG).map(([k,v]) =>
                                    <option key={k} value={k}>{v.label}</option>)}
                            </Form.Select>
                        )}
                        {!isHR && isMine && (
                            <>
                                {data.status==='resolved' && (
                                    <>
                                        <Button size="sm" variant="success" onClick={() => setShowClose(true)}>
                                            <CheckCircle size={14} className="me-1"/>Close Ticket
                                        </Button>
                                        <Button size="sm" variant="outline-warning" onClick={reopen}>
                                            <RefreshCw size={14} className="me-1"/>Reopen
                                        </Button>
                                    </>
                                )}
                                {data.status==='closed' && <Badge bg="success" className="px-3 py-2">Closed ✓</Badge>}
                            </>
                        )}
                    </div>
                </div>
                {data.sla_due_at && (
                    <div className="mt-2 small text-muted d-flex gap-3 flex-wrap">
                        <span><Clock size={12} className="me-1"/>SLA due: {formatDate(data.sla_due_at)}</span>
                        {data.first_response_at && <span>First response: {formatDate(data.first_response_at)}</span>}
                        {data.resolved_at && <span>Resolved: {formatDate(data.resolved_at)}</span>}
                    </div>
                )}
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white fw-bold border-0 pt-3">
                    Conversation ({data.messages?.length||0} messages)
                </Card.Header>
                <Card.Body style={{maxHeight:480,overflowY:'auto'}}>
                    {(data.messages||[]).map(m => {
                        const isMe = m.sender_id===user?.id;
                        return (
                            <div key={m.id} className={`mb-3 d-flex ${isMe?'justify-content-end':''}`}>
                                <div style={{
                                    maxWidth:'80%',
                                    background:m.is_internal?'#fef9c3':isMe?'#eef2ff':'#f8fafc',
                                    border:m.is_internal?'1px dashed #d97706':'1px solid #e5e7eb',
                                    borderRadius:12, padding:'10px 14px',
                                }}>
                                    {m.is_internal && (
                                        <div className="text-warning fw-bold" style={{fontSize:10}}>🔒 INTERNAL NOTE</div>
                                    )}
                                    <div className="fw-medium small mb-1">
                                        {m.sender_name}
                                        <span className="text-muted fw-normal ms-2" style={{fontSize:10}}>
                                            {formatDate(m.created_at)}
                                        </span>
                                    </div>
                                    <div style={{fontSize:14,whiteSpace:'pre-wrap'}}>{m.message}</div>
                                    {(m.attachments||[]).length>0 && (
                                        <div className="mt-2">
                                            {m.attachments.map((a,i) => (
                                                <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-secondary py-0 me-1">
                                                    📎 {a.name}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef}/>
                </Card.Body>
                {isActive && (
                    <Card.Footer className="bg-white p-3">
                        {isHR && (
                            <Form.Check className="mb-2 small" type="switch"
                                label="Internal note (not visible to employee)"
                                checked={internal}
                                onChange={e => setInternal(e.target.checked)}/>
                        )}
                        <InputGroup>
                            <Form.Control as="textarea" rows={2}
                                placeholder={internal?'Add an internal note for HR team…':'Type your reply…'}
                                value={reply}
                                onChange={e => setReply(e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter'&&e.ctrlKey) sendReply(); }}
                                style={{resize:'none',background:internal?'#fffbeb':undefined}}/>
                            <Button variant="primary" onClick={sendReply}
                                disabled={sending||!reply.trim()}>
                                {sending?<Spinner size="sm"/>:<Send size={16}/>}
                            </Button>
                        </InputGroup>
                        <div className="text-muted mt-1" style={{fontSize:11}}>Ctrl+Enter to send</div>
                    </Card.Footer>
                )}
            </Card>

            {data.status==='closed' && data.satisfaction_rating && (
                <Card className="border-0 shadow-sm p-4">
                    <h6 className="fw-bold mb-2">Your Satisfaction Rating</h6>
                    <StarRating value={data.satisfaction_rating} readOnly/>
                    {data.satisfaction_comment && (
                        <p className="text-muted small mt-2">{data.satisfaction_comment}</p>
                    )}
                </Card>
            )}

            <Modal show={showClose} onHide={() => setShowClose(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">Close Ticket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">Was your issue resolved? Rate your experience to help HR improve.</p>
                    <div className="mb-3">
                        <Form.Label className="small fw-bold">Overall satisfaction</Form.Label>
                        <StarRating value={rating} onChange={setRating}/>
                    </div>
                    <Form.Control as="textarea" rows={2}
                        placeholder="Any feedback for HR? (optional)"
                        value={ratingComment}
                        onChange={e => setRatingComment(e.target.value)}/>
                    <div className="d-flex gap-2 mt-4">
                        <Button variant="light" className="flex-grow-1" onClick={() => setShowClose(false)}>Cancel</Button>
                        <Button variant="success" className="flex-grow-1" onClick={closeTicket}>
                            <CheckCircle size={15} className="me-1"/>Close Ticket
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE BASE — window.confirm → ConfirmModal for deleteArticle
// ════════════════════════════════════════════════════════════════════════════
const KnowledgeBase = ({ isHR }) => {
    const [articles,    setArticles]    = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState('');
    const [selected,    setSelected]    = useState(null);
    const [showEditor,  setShowEditor]  = useState(false);
    const [editArticle, setEditArticle] = useState(null);
    const [confirm,     setConfirm]     = useState({ show:false, id:null, title:'', loading:false });

    const load = (q=search) => {
        setLoading(true);
        axiosInstance.get('/helpdesk/kb',{params:{search:q||undefined}})
            .then(r => setArticles(r.data.data?.articles||[]))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const markHelpful = async (id, helpful) => {
        try {
            await axiosInstance.post(`/helpdesk/kb/${id}/helpful`,{helpful});
            toast.success(helpful?'👍 Marked as helpful':'👎 Feedback recorded');
            load();
        } catch { toast.error('Failed'); }
    };

    const handleDeleteConfirm = async () => {
        setConfirm(p=>({...p,loading:true}));
        try {
            await axiosInstance.delete(`/helpdesk/kb/${confirm.id}`);
            toast.success('Article deleted'); load();
        } catch { toast.error('Delete failed'); }
        finally { setConfirm({show:false,id:null,title:'',loading:false}); }
    };

    if (selected) return (
        <ArticleView article={selected} isHR={isHR}
            onBack={() => setSelected(null)}
            onHelpful={markHelpful}
            onEdit={(a) => { setEditArticle(a); setShowEditor(true); setSelected(null); }}/>
    );

    return (
        <>
            <div className="d-flex gap-2 mb-4 align-items-center">
                <InputGroup style={{maxWidth:360}}>
                    <InputGroup.Text><Search size={14}/></InputGroup.Text>
                    <Form.Control placeholder="Search knowledge base…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key==='Enter' && load(search)}/>
                    <Button variant="primary" onClick={() => load(search)}>Search</Button>
                </InputGroup>
                {isHR && (
                    <Button variant="success" size="sm" className="ms-auto"
                        onClick={() => { setEditArticle(null); setShowEditor(true); }}>
                        <Plus size={14} className="me-1"/>New Article
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : (
                <Row className="g-3">
                    {articles.length===0 ? (
                        <Col xs={12}>
                            <Card className="border-0 shadow-sm p-5 text-center text-muted">
                                <BookOpen size={36} className="mx-auto mb-2 opacity-50"/>
                                <p>No articles found{search && ` for "${search}"`}</p>
                            </Card>
                        </Col>
                    ) : articles.map(a => (
                        <Col key={a.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100" style={{cursor:'pointer'}}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between mb-2">
                                        {a.category_name && (
                                            <Badge bg="light" text="dark" style={{fontSize:10}}>
                                                {a.category_name}
                                            </Badge>
                                        )}
                                        {!a.is_published && isHR && (
                                            <Badge bg="warning" style={{fontSize:10}}>Draft</Badge>
                                        )}
                                    </div>
                                    <h6 className="fw-bold mb-2">{a.title}</h6>
                                    <p className="text-muted small mb-3" style={{
                                        overflow:'hidden',display:'-webkit-box',
                                        WebkitLineClamp:3,WebkitBoxOrient:'vertical',
                                    }}>
                                        {a.excerpt?.replace(/<[^>]+>/g,'')||''}
                                    </p>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted" style={{fontSize:11}}>
                                            <Eye size={11} className="me-1"/>{a.view_count}
                                            <span className="ms-2">👍 {a.helpful_yes}</span>
                                        </div>
                                        <div className="d-flex gap-1">
                                            {isHR && (
                                                <>
                                                    <Button size="sm" variant="outline-secondary" className="py-0"
                                                        onClick={e => { e.stopPropagation(); setEditArticle(a); setShowEditor(true); }}>
                                                        <Edit2 size={12}/>
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" className="py-0"
                                                        onClick={e => { e.stopPropagation(); setConfirm({show:true,id:a.id,title:a.title,loading:false}); }}>
                                                        <Trash2 size={12}/>
                                                    </Button>
                                                </>
                                            )}
                                            <Button size="sm" variant="outline-primary" className="py-0"
                                                onClick={() => setSelected(a)}>
                                                Read <ChevronRight size={12}/>
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {showEditor && (
                <ArticleEditorModal
                    article={editArticle}
                    onClose={() => setShowEditor(false)}
                    onDone={() => { setShowEditor(false); load(); }}/>
            )}

            <ConfirmModal
                show={confirm.show}
                title="Delete Article"
                message={`Delete "${confirm.title}"? This cannot be undone.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirm({show:false,id:null,title:'',loading:false})}
                loading={confirm.loading}/>
        </>
    );
};

// ── Article View — unchanged ──────────────────────────────────────────────
const ArticleView = ({ article, isHR, onBack, onHelpful, onEdit }) => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axiosInstance.get(`/helpdesk/kb/${article.id}`)
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load article'))
            .finally(() => setLoading(false));
    }, [article.id]);
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (!data)   return null;
    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>← Back</Button>
            <Card className="border-0 shadow-sm p-4">
                <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
                    <div>
                        {data.category_name && (
                            <Badge bg="light" text="dark" className="mb-2">{data.category_name}</Badge>
                        )}
                        <h4 className="fw-bold">{data.title}</h4>
                        <div className="text-muted small">
                            By {data.author_name} · {formatDate(data.updated_at||data.created_at)}
                            <span className="ms-3"><Eye size={12} className="me-1"/>{data.view_count} views</span>
                        </div>
                    </div>
                    {isHR && (
                        <Button size="sm" variant="outline-secondary" onClick={() => onEdit(data)}>
                            <Edit2 size={14} className="me-1"/>Edit
                        </Button>
                    )}
                </div>
                <div className="mb-4" style={{lineHeight:1.7}}
                    dangerouslySetInnerHTML={{__html:data.content}}/>
                {(data.tags||[]).length > 0 && (
                    <div className="mb-4 d-flex gap-1 flex-wrap">
                        <Tag size={14} className="text-muted me-1"/>
                        {data.tags.map((t,i) => <Badge key={i} bg="light" text="dark">{t}</Badge>)}
                    </div>
                )}
                <div className="pt-3 border-top d-flex align-items-center gap-3">
                    <span className="text-muted small">Was this helpful?</span>
                    <Button size="sm" variant="outline-success" onClick={() => onHelpful(data.id,true)}>
                        👍 Yes ({data.helpful_yes})
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => onHelpful(data.id,false)}>
                        👎 No ({data.helpful_no})
                    </Button>
                </div>
            </Card>
        </div>
    );
};

// ── Article Editor Modal — VALIDATIONS ────────────────────────────────────
const validateArticle = (form) => {
    const e = {};
    const title = form.title?.trim();
    if (!title)              e.title = 'Title is required';
    else if (title.length<5) e.title = 'Title must be at least 5 characters';
    else if (title.length>255) e.title = 'Title cannot exceed 255 characters';

    const content = form.content?.trim();
    if (!content)               e.content = 'Article content is required';
    else if (content.length<50) e.content = 'Content must be at least 50 characters';

    return e;
};

const ArticleEditorModal = ({ article, onClose, onDone }) => {
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({
        title:          article?.title       || '',
        content:        article?.content     || '',
        category_id:    article?.category_id || '',
        tags:           article?.tags ? article.tags.join(', ') : '',
        is_published:   article?.is_published || false,
    });
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);

    useEffect(() => {
        axiosInstance.get('/helpdesk/categories')
            .then(r => setCategories(r.data.data||[]))
            .catch(()=>{});
    }, []);

    const setField = (k,v) => {
        setForm(p=>({...p,[k]:v}));
        if (attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; return n; });
    };

    const submit = async () => {
        setAttempted(true);
        const errs = validateArticle(form);
        if (Object.keys(errs).length>0) { setErrors(errs); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                tags: form.tags ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : [],
            };
            if (article) {
                await axiosInstance.put(`/helpdesk/kb/${article.id}`, payload);
                toast.success('Article updated');
            } else {
                await axiosInstance.post('/helpdesk/kb', payload);
                toast.success(form.is_published ? 'Article published' : 'Draft saved');
            }
            onDone();
        } catch (err) { toast.error(err.response?.data?.message||'Failed to save'); }
        finally { setSaving(false); }
    };

    const titleLen   = form.title?.length||0;
    const contentLen = form.content?.length||0;

    return (
        <Modal show onHide={onClose} centered size="xl">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    {article ? 'Edit Article' : 'New Knowledge Base Article'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Title * <span className="text-muted fw-normal">(min 5 chars)</span></span>
                            <span className={titleLen>230?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {titleLen}/255
                            </span>
                        </Form.Label>
                        <Form.Control size="lg"
                            placeholder="e.g. How to apply for casual leave"
                            value={form.title} maxLength={255}
                            isInvalid={attempted && !!errors.title}
                            onChange={e => setField('title', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Category</Form.Label>
                        <Form.Select value={form.category_id}
                            onChange={e => setField('category_id', e.target.value)}>
                            <option value="">— No category —</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Tags (comma-separated)</Form.Label>
                        <Form.Control placeholder="e.g. leave, policy, casual"
                            value={form.tags}
                            onChange={e => setField('tags', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Content * <span className="text-muted fw-normal">(min 50 chars, HTML supported)</span></span>
                            <span className={contentLen>4500?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {contentLen} chars
                            </span>
                        </Form.Label>
                        <Form.Control as="textarea" rows={14}
                            placeholder="Write the article content here. HTML is supported."
                            value={form.content}
                            isInvalid={attempted && !!errors.content}
                            onChange={e => setField('content', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.content}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12}>
                        <Form.Check type="switch"
                            label="Publish immediately (uncheck to save as draft)"
                            checked={form.is_published}
                            onChange={e => setField('is_published', e.target.checked)}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : form.is_published ? '📢 Publish' : '💾 Save Draft'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS VIEW — unchanged
// ════════════════════════════════════════════════════════════════════════════
const StatsView = () => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axiosInstance.get('/helpdesk/tickets/stats')
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load stats'))
            .finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (!data)   return null;
    const s = data.summary||{};
    const kpis = [
        { label:'Total Tickets',    val:s.total||0,                                         color:'#4f46e5' },
        { label:'Open',             val:s.open||0,                                          color:'#d97706' },
        { label:'SLA Breached',     val:s.sla_breached||0,                                  color:'#dc2626' },
        { label:'Avg Resolution',   val:s.avg_resolution_hours?`${s.avg_resolution_hours}h`:'—', color:'#059669' },
        { label:'First Response',   val:s.avg_first_response_minutes?`${s.avg_first_response_minutes}m`:'—', color:'#0891b2' },
        { label:'Satisfaction',     val:s.avg_satisfaction?`${s.avg_satisfaction}/5 ⭐`:'—', color:'#7c3aed' },
    ];
    return (
        <>
            <Row className="g-3 mb-4">
                {kpis.map((k,i) => (
                    <Col key={i} xs={6} md={4} lg={2}>
                        <Card className="border-0 shadow-sm p-3 text-center"
                            style={{borderBottom:`3px solid ${k.color}`}}>
                            <div style={{fontSize:22,fontWeight:700,color:k.color}}>{k.val}</div>
                            <div className="text-muted" style={{fontSize:11}}>{k.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Row className="g-4">
                <Col md={6}>
                    <Card className="border-0 shadow-sm p-4">
                        <h6 className="fw-bold mb-3">By Category</h6>
                        {(data.by_category||[]).map((c,i) => (
                            <div key={i} className="d-flex justify-content-between align-items-center mb-2 small">
                                <span>{c.name||'Uncategorised'}</span>
                                <div className="d-flex gap-2">
                                    {c.open>0 && <Badge bg="warning">{c.open} open</Badge>}
                                    <Badge bg="light" text="dark">{c.count} total</Badge>
                                </div>
                            </div>
                        ))}
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="border-0 shadow-sm p-4">
                        <h6 className="fw-bold mb-3">By Priority (open tickets)</h6>
                        {(data.by_priority||[]).map((p,i) => (
                            <div key={i} className="mb-2">
                                <div className="d-flex justify-content-between small mb-1">
                                    <Badge bg={PRIORITY_CONFIG[p.priority]?.bg||'secondary'}>
                                        {PRIORITY_CONFIG[p.priority]?.label||p.priority}
                                    </Badge>
                                    <span className="fw-bold">{p.count}</span>
                                </div>
                                <div style={{height:6,background:'#e5e7eb',borderRadius:3}}>
                                    <div style={{
                                        width:`${data.by_priority[0]?.count?(p.count/data.by_priority[0].count)*100:0}%`,
                                        height:'100%',
                                        background:['#dc2626','#d97706','#0891b2','#6b7280'][i]||'#4f46e5',
                                        borderRadius:3,
                                    }}/>
                                </div>
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default HelpdeskPage;
